import React, { useState } from 'react';
import { Loader2, Plus, Trash2, Clock, CalendarX, Globe } from 'lucide-react';
import api, { errText } from '../lib/api';
import { useToast } from '../hooks/use-toast';

const DAYS = [['Mon', 0], ['Tue', 1], ['Wed', 2], ['Thu', 3], ['Fri', 4], ['Sat', 5], ['Sun', 6]];
const TZ_LIST = (() => {
  try { if (typeof Intl.supportedValuesOf === 'function') return Intl.supportedValuesOf('timeZone'); } catch { /* noop */ }
  return ['UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'Europe/London', 'Europe/Berlin', 'Europe/Paris', 'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore',
    'Asia/Tokyo', 'Australia/Sydney'];
})();
const browserTz = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'UTC'; } };

const AvailabilityEditor = ({ teacher, onSaved }) => {
  const { toast } = useToast();
  const [tz, setTz] = useState(teacher.consult_timezone || browserTz());
  const [byDay, setByDay] = useState(() => {
    const m = {}; DAYS.forEach(([, d]) => { m[d] = []; });
    (teacher.weekly_availability || []).forEach((w) => { (m[w.weekday] = m[w.weekday] || []).push({ start: w.start, end: w.end }); });
    return m;
  });
  const [blackouts, setBlackouts] = useState(teacher.blackouts || []);
  const [saving, setSaving] = useState(false);

  const addWin = (d) => setByDay((p) => ({ ...p, [d]: [...(p[d] || []), { start: '09:00', end: '12:00' }] }));
  const rmWin = (d, i) => setByDay((p) => ({ ...p, [d]: p[d].filter((_, idx) => idx !== i) }));
  const setWin = (d, i, k, v) => setByDay((p) => ({ ...p, [d]: p[d].map((w, idx) => (idx === i ? { ...w, [k]: v } : w)) }));

  const addBlackout = () => setBlackouts((p) => [...p, { start_date: '', end_date: '', note: '' }]);
  const rmBlackout = (i) => setBlackouts((p) => p.filter((_, idx) => idx !== i));
  const setBlackout = (i, k, v) => setBlackouts((p) => p.map((b, idx) => (idx === i ? { ...b, [k]: v } : b)));

  const save = async () => {
    const weekly = [];
    for (const [, d] of DAYS) for (const w of (byDay[d] || [])) {
      if (!w.start || !w.end) continue;
      if (w.start >= w.end) return toast({ title: `Fix ${DAYS.find((x) => x[1] === d)[0]}: end must be after start` });
      weekly.push({ weekday: d, start: w.start, end: w.end });
    }
    const bl = blackouts.filter((b) => b.start_date).map((b) => ({ start_date: b.start_date, end_date: b.end_date || b.start_date, note: (b.note || '').trim() || null }));
    setSaving(true);
    try {
      const r = await api.patch(`/consultations/my-availability/${teacher.id}`, { timezone: tz, weekly, blackouts: bl });
      toast({ title: 'Availability saved' }); onSaved && onSaved(r.data);
    } catch (e) { toast({ title: 'Could not save', description: errText(e) }); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-3xl bg-white/70 border border-rose/40 p-5" data-testid="availability-editor">
      <h3 className="font-serif-display text-xl text-burgundy flex items-center gap-2"><Clock className="w-5 h-5 text-rose-italic" /> {teacher.name} — weekly availability</h3>
      <p className="text-xs text-mahogany/60 mt-1">Visitors can only book inside these hours. Times are in your timezone; each visitor sees them converted to their own. Bookable start times are generated from your session lengths with a 10-minute gap, up to 12 weeks ahead.</p>

      <label className="mt-4 block text-xs uppercase tracking-widest text-mahogany/70"><Globe className="w-3.5 h-3.5 inline mr-1" /> Your timezone
        <select value={tz} onChange={(e) => setTz(e.target.value)} className="mt-1 w-full sm:w-96 bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid="availability-timezone">
          {!TZ_LIST.includes(tz) && <option value={tz}>{tz}</option>}
          {TZ_LIST.map((z) => <option key={z} value={z}>{z}</option>)}
        </select>
      </label>

      <div className="mt-4 space-y-2">
        {DAYS.map(([label, d]) => (
          <div key={d} className="flex flex-wrap items-start gap-2 py-1.5 border-b border-rose/15" data-testid={`avail-day-${d}`}>
            <div className="w-12 text-sm font-medium text-burgundy pt-2">{label}</div>
            <div className="flex-1 space-y-2">
              {(byDay[d] || []).length === 0 && <span className="text-xs text-mahogany/40 pt-2 inline-block">Unavailable</span>}
              {(byDay[d] || []).map((w, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`avail-window-${d}-${i}`}>
                  <input type="time" value={w.start} onChange={(e) => setWin(d, i, 'start', e.target.value)} className="bg-white rounded-lg px-2 py-1.5 border border-rose/50 text-sm" data-testid={`avail-start-${d}-${i}`} />
                  <span className="text-mahogany/50 text-sm">to</span>
                  <input type="time" value={w.end} onChange={(e) => setWin(d, i, 'end', e.target.value)} className="bg-white rounded-lg px-2 py-1.5 border border-rose/50 text-sm" data-testid={`avail-end-${d}-${i}`} />
                  <button type="button" onClick={() => rmWin(d, i)} className="text-red-500/70 hover:text-red-600 p-1" data-testid={`avail-rm-${d}-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => addWin(d)} className="pill-btn-outline text-xs py-1 mt-0.5" data-testid={`avail-add-${d}`}><Plus className="w-3.5 h-3.5" /> Hours</button>
          </div>
        ))}
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between">
          <span className="text-xs uppercase tracking-widest text-mahogany/70 flex items-center gap-1"><CalendarX className="w-3.5 h-3.5" /> Time off (overrides weekly hours)</span>
          <button type="button" onClick={addBlackout} className="pill-btn-outline text-xs py-1" data-testid="blackout-add"><Plus className="w-3.5 h-3.5" /> Add dates</button>
        </div>
        <div className="mt-2 space-y-2">
          {blackouts.length === 0 && <p className="text-xs text-mahogany/50">No blocked dates. Add a date or date range you're away — those days won't be bookable.</p>}
          {blackouts.map((b, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 rounded-2xl bg-white border border-rose/30 p-2" data-testid={`blackout-row-${i}`}>
              <input type="date" value={b.start_date || ''} onChange={(e) => setBlackout(i, 'start_date', e.target.value)} className="bg-cream/50 rounded-lg px-2 py-1.5 border border-rose/50 text-sm" data-testid={`blackout-start-${i}`} />
              <span className="text-mahogany/50 text-sm">to</span>
              <input type="date" value={b.end_date || ''} onChange={(e) => setBlackout(i, 'end_date', e.target.value)} className="bg-cream/50 rounded-lg px-2 py-1.5 border border-rose/50 text-sm" data-testid={`blackout-end-${i}`} />
              <input value={b.note || ''} onChange={(e) => setBlackout(i, 'note', e.target.value)} placeholder="Note (optional)" className="flex-1 min-w-[120px] bg-cream/50 rounded-lg px-2 py-1.5 border border-rose/50 text-sm" />
              <button type="button" onClick={() => rmBlackout(i)} className="text-red-500/70 hover:text-red-600 p-1" data-testid={`blackout-rm-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>

      <button onClick={save} disabled={saving} className="pill-btn mt-5" data-testid="availability-save">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save availability</button>
    </div>
  );
};

export default AvailabilityEditor;
